import { Component } from 'vue-property-decorator'
import { mixins } from 'vue-class-component'

import { casePriority } from '@/modules/Newcase/helpers/case'
import { ApolloResponse } from '@/interfaces/apollo'
import { Breadcrumb } from '@/interfaces/breadcrumbs'
import { Case } from '@/modules/Newcase/interfaces/case'
import { showErrors, showNotification } from '@/utils/snack'
import { dateTimeToDisplay, dateToDisplay, dateToNow } from '@/utils/dateTime'
import AppBreadcrumbs from '@/components/AppBreadcrumbs.vue'

import CaseDialog from '@/modules/Newcase/scripts/case-dialog'
import CaseDetailDetail from '@/modules/Newcase/views/detail.vue'
import { getCaseById, editCaseById } from '@/modules/Newcase/gql/case.gql'
import CaseSteps from '@/modules/Newcase/components/CaseSteps.vue'
import CaseFoldingCard from '@/modules/Newcase/components/CaseFoldingCard.vue'

import dollarFilter from '@/utils/dollarFilter'
import AccountAssets from '@/modules/Newcase/components/AccountAssets.vue'
import AccountCertificates from '@/modules/Newcase/components/AccountCertificates.vue'
import AppAttachmentsCard from '@/components/AppAttachmentsCard.vue'
import AccountInfo from '@/modules/Newcase/components/AccountInfo.vue'
import CasePanel from '@/modules/Newcase/components/CasePanel.vue'
import CaseDetailInquiry from '@/modules/Newcase/views/inquiry.vue'
import CaseDetailQuoteAndProposal from '@/modules/Newcase/views/quoteAndProposal.vue'
import CaseDetailBinding from '@/modules/Newcase/views/binding.vue'
import CaseDetailCompleted from '@/modules/Newcase/views/completed.vue'
import CaseDetailTask from '@/modules/Newcase/views/postBinding.vue'
import CaseNoteDrawer from '@/modules/Newcase/components/CaseNoteDrawer.vue'
import AssignToDialog from '@/components/AssignToDialog.vue'
import { SnackType } from '@/interfaces/snack'

@Component({
  components: {
    AppBreadcrumbs,
    CaseDetailDetail,
    CaseSteps,
    CaseFoldingCard,
    AccountAssets,
    AccountCertificates,
    AppAttachmentsCard,
    AccountInfo,
    CasePanel,
    CaseDetailInquiry,
    CaseDetailQuoteAndProposal,
    CaseDetailBinding,
    CaseDetailCompleted,
    CaseDetailTask,
    CaseNoteDrawer,
    AssignToDialog
  }
})
export default class NewcaseDetail extends mixins(CaseDialog) {
  breadcrumbs: Breadcrumb[] = [
    {
      text: 'Insurance Cases'
    },
    {
      text: 'Case2.0',
      to: { name: 'NewcaseDashboard' }
    }
  ]

  initState = true

  caseInfo: Case | null = null
  activeStaff = false
  showActionDialog = false

  stepItemsSales = [
    'Client & Inquiry',
    'Quote & Proposal',
    'Binding',
    'Review',
    'Post Binding'
  ]

  completeDialog = {
    show: false
  }

  showNoteDrawer = false
  noteDrawerTabIndex = 0

  dollarFilter = dollarFilter

  get attachmentList () {
    if (!this.caseInfo) {
      return []
    }
    return this.caseInfo.account.attachments || []
  }

  get stepSalesIndex () {
    const { caseInfo } = this
    if (!caseInfo) {
      return 0
    }
    return caseInfo.step
  }

  get stepServiceIndex () {
    const { caseInfo } = this
    if (!caseInfo) {
      return -1
    }
    return caseInfo.step - 4
  }

  dateTimeToDisplay = dateTimeToDisplay
  dateToDisplay = dateToDisplay
  dateToNow = dateToNow

  casePriority = casePriority

  mounted () {
    if (this.caseInfo) {
      this.breadcrumbs.push({
        text: `${this.caseInfo.inquiryName} (Case ID: ${this.caseInfo.id})`
      })
    }
    this.getCase()
  }

  async deleteCaseConfirm () {
    await this.deleteCasePost()
    this.showActionDialog = false
    this.$router.replace({
      name: 'NewcaseDashboard'
    })
  }

  async assignCaseConfirm (userIds: number[]) {
    await this.assignCasePost(userIds)
    this.showActionDialog = false
    this.getCase()
  }

  async assignCaseCancel () {
    this.assignDialog.show = false
    this.showActionDialog = false
  }

  async closeCaseConfirm () {
    if (this.closeCaseDialog.isClose) {
      await this.closeCasePost()
    } else {
      await this.openCasePost()
    }
    this.showActionDialog = false
    this.getCase()
  }

  async switchCaseStatusConfirm () {
    await this.switchCaseStatusPost()
    this.showActionDialog = false
    this.getCase()
  }

  onCancel () {
    this.showActionDialog = false
  }

  async sendImportantChangeRequest (wfCase: Case, important: boolean) {
    try {
      const res = await this.$apollo.mutate({
        mutation: editCaseById,
        variables: {
          caseId: +wfCase.id,
          caseInput: {
            inquiryName: wfCase.inquiryName,
            important
          }
        }
      })
      this.getCase()
      showNotification({
        msg: 'Update successfully',
        type: SnackType.Success
      })
    } catch (e) {
      showErrors(e.graphQLErrors)
    }
  }

  // 编辑紧急性

  async sendUrgentChangeRequest (wfCase: Case, urgent: boolean) {
    try {
      const res = await this.$apollo.mutate({
        mutation: editCaseById,
        variables: {
          caseId: +wfCase.id,
          caseInput: {
            inquiryName: wfCase.inquiryName,
            urgent
          }
        }
      })
      this.getCase()
      showNotification({
        msg: 'Update successfully',
        type: SnackType.Success
      })
    } catch (e) {
      showErrors(e.graphQLErrors)
    }
  }

  async getCase () {
    try {
      const result: ApolloResponse = await this.$apollo.query({
        query: getCaseById,
        variables: {
          caseId: Number(this.$route.params.id)
        }
      })
      if (!result.data) {
        showErrors(result.errors)
        return
      }
      this.caseInfo = result.data.getCaseById
      if (this.breadcrumbs.length === 2) {
        this.breadcrumbs.push({
          text: `${(this.caseInfo as Case).inquiryName} (Case ID: ${(this.caseInfo as Case).id})`
        })
      }
    } catch (e) {
      showErrors(e.graphQLErrors)
    }
  }

  routeToAccountDetail () {
    if (this.caseInfo && this.caseInfo.account) {
      this.$router.push({
        name: 'AccountDetailDetail',
        params: {
          id: this.caseInfo.account.id + ''
        }
      })
    }
  }

  onSideBarClick (index: number) {
    this.noteDrawerTabIndex = index
    this.showNoteDrawer = true
  }

  onSideBarClose () {
    this.showNoteDrawer = false
  }

  onSuccess () {
    this.getCase()
  }

  async addAsset () {
    this.getCase()
  }
}
